import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, Badge, Button, Input, Spinner } from '../../components/ui';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';

interface MicrositeDetail {
  id?: number;
  title: string;
  status: string;
  html_content: string | null;
}

interface MagicLinkRedeemResponse {
  public_token: string;
  redirect_path: string;
}

const IN_PROGRESS_STATUSES = new Set(['queued', 'generating']);
const PREVIEW_IFRAME_SANDBOX = 'allow-scripts allow-downloads allow-popups allow-popups-to-escape-sandbox';

function statusVariant(status: string): 'success' | 'danger' | 'warning' | 'secondary' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'queued' || status === 'generating') return 'warning';
  return 'secondary';
}

export default function MicrositePreviewPage() {
  const { id, publicToken, token } = useParams<{ id?: string; publicToken?: string; token?: string }>();
  const navigate = useNavigate();
  const { push: addToast } = useToasts();
  const isMagicPreview = Boolean(token);
  const isPublicTokenPreview = Boolean(publicToken);
  const isPublicPreview = isMagicPreview || isPublicTokenPreview;
  const [email, setEmail] = useState('');
  const [accessRequestResult, setAccessRequestResult] = useState<{ status: 'queued' | 'denied'; message: string } | null>(null);

  const privateQuery = useQuery<MicrositeDetail>({
    queryKey: ['microsite-detail', Number(id)],
    queryFn: () => {
      return AXIOS_INSTANCE.get(`/v1/microsite-generator/microsites/${id}`).then((r) => r.data);
    },
    enabled: !!id && !isPublicPreview,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && IN_PROGRESS_STATUSES.has(status) ? 3000 : false;
    },
  });
  const publicQuery = useQuery<MicrositeDetail>({
    queryKey: ['microsite-public-preview', publicToken],
    queryFn: () =>
      AXIOS_INSTANCE.get(`/v1/microsite-generator/public/microsites/${publicToken}`).then((r) => r.data),
    enabled: isPublicTokenPreview && !!publicToken,
    retry: false,
  });

  const redeemQuery = useQuery<MagicLinkRedeemResponse>({
    queryKey: ['microsite-magic-redeem', token],
    queryFn: () =>
      AXIOS_INSTANCE.post(`/v1/microsite-generator/public/magic-links/${token}/redeem`).then((r) => r.data),
    enabled: isMagicPreview && !!token,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (redeemQuery.data?.redirect_path) {
      navigate(redeemQuery.data.redirect_path, { replace: true });
    }
  }, [navigate, redeemQuery.data?.redirect_path]);

  const accessRequestMutation = useMutation({
    mutationFn: (recipientEmail: string) =>
      AXIOS_INSTANCE.post(`/v1/microsite-generator/public/microsites/${publicToken}/access-requests`, {
        email: recipientEmail,
      }).then((r) => r.data),
    onSuccess: (data) => {
      setAccessRequestResult(data);
    },
  });

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    addToast({ kind: 'success', message: 'Link copied to clipboard' });
  };

  const handleAccessRequest = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    accessRequestMutation.mutate(email);
  };

  const data = isPublicTokenPreview ? publicQuery.data : privateQuery.data;
  const isLoading = isMagicPreview || isPublicTokenPreview ? publicQuery.isLoading || redeemQuery.isLoading : privateQuery.isLoading;
  const isError = isMagicPreview ? redeemQuery.isError : isPublicTokenPreview ? publicQuery.isError : privateQuery.isError;
  const accessRequired =
    isPublicTokenPreview &&
    (publicQuery.error as { response?: { status?: number } } | null)?.response?.status === 401;

  useEffect(() => {
    if (!data?.title) return;
    const previousTitle = document.title;
    document.title = data.title;
    return () => {
      document.title = previousTitle;
    };
  }, [data?.title]);

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-dark-bg overflow-hidden">
      {!isPublicPreview && (
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface">
          <button
            onClick={() => navigate('/microsite-generator')}
            className="flex items-center gap-1.5 text-small text-gray-500 dark:text-gray-400 hover:text-charcoal dark:hover:text-gray-100 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>

          <div className="w-px h-4 bg-gray-200 dark:bg-dark-border" />
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {isLoading ? (
              <div className="h-4 w-48 bg-gray-200 dark:bg-dark-surface-2 animate-pulse rounded" />
            ) : (
              <>
                <span className="text-small font-medium text-charcoal dark:text-gray-100 truncate">
                  {data?.title ?? '—'}
                </span>
                {data && (
                  <Badge variant={statusVariant(data.status)}>{data.status}</Badge>
                )}
              </>
            )}
          </div>

          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 text-small text-gray-500 dark:text-gray-400 hover:text-charcoal dark:hover:text-gray-100 transition-colors"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy link
          </button>
        </div>
      )}

      {/* Preview area */}
      <div className="flex-1 min-h-0 flex items-center justify-center">
        {isLoading && <Spinner size="lg" />}

        {isMagicPreview && redeemQuery.isError && (
          <div className="max-w-md w-full px-4">
            <Alert variant="error">This preview link is invalid or has already been used.</Alert>
          </div>
        )}

        {accessRequired && (
          <div className="max-w-md w-full px-4">
            {accessRequestResult ? (
              <Alert variant={accessRequestResult.status === 'denied' ? 'error' : 'success'}>
                {accessRequestResult.status === 'denied'
                  ? accessRequestResult.message
                  : 'Check your email for a preview access link.'}
              </Alert>
            ) : (
              <form
                onSubmit={handleAccessRequest}
                className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-5 space-y-4"
              >
                <div>
                  <h1 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
                    Request preview access
                  </h1>
                  <p className="mt-1 text-small text-gray-500 dark:text-gray-400">
                    Enter your email and we'll send a secure access link.
                  </p>
                </div>
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
                {accessRequestMutation.isError && (
                  <Alert variant="error">Unable to send access email. Please try again.</Alert>
                )}
                <Button
                  type="submit"
                  disabled={accessRequestMutation.isPending}
                  className="w-full justify-center"
                >
                  {accessRequestMutation.isPending ? 'Sending...' : 'Send access link'}
                </Button>
              </form>
            )}
          </div>
        )}

        {!accessRequired && !isMagicPreview && isError && (
          <div className="max-w-md w-full px-4">
            <Alert variant="error">Failed to load microsite. It may have been deleted.</Alert>
          </div>
        )}

        {!isMagicPreview && !isLoading && !isError && !accessRequired && data?.status !== 'completed' && (
          <div className="max-w-md w-full px-4">
            <Alert variant="info">
              This microsite is not ready yet (status: {data?.status}). Check back shortly.
            </Alert>
          </div>
        )}

        {!isLoading && data?.status === 'completed' && data.html_content && (
          <iframe
            srcDoc={data.html_content}
            sandbox={PREVIEW_IFRAME_SANDBOX}
            referrerPolicy="no-referrer"
            title={data.title}
            className="w-full h-full border-0"
          />
        )}
      </div>
    </div>
  );
}
